// src/services/api.ts
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bullxch.com/api';
console.log(API_URL);
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Track if we're currently refreshing the token to prevent infinite loops
let isRefreshing = false;
let failedQueue: { resolve: Function; reject: Function }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('privy:token');
    
    // If token exists, add it to headers for all requests
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is due to an invalid/expired token and we haven't tried refreshing yet
    if (error.response && 
        (error.response.status === 401 || error.response.status === 403) &&
        !originalRequest._retry) {
      
      // If we're not currently refreshing
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;
        
        try {
          // Get the user ID from storage
          const privyUserId = localStorage.getItem('privy:token')?.split('.')[2]; // Extract from JWT
          
          if (!privyUserId) {
            // If no privy user ID, we can't refresh
            processQueue(new Error('No user ID available for token refresh'));
            return Promise.reject(error);
          }
          
          // Call the refresh endpoint
          const response = await axios.post(`${API_URL}/auth/refresh`, { privyUserId });
          
          if (response.data && response.data.token) {
            // Store the new token
            localStorage.setItem('auth_token', response.data.token);
            
            // Update the original request with new token
            originalRequest.headers['x-auth-token'] = response.data.token;
            
            // Process all queued requests with the new token
            processQueue(null, response.data.token);
            
            // Return the original request with new token
            return api(originalRequest);
          } else {
            // If refresh failed, process queue with error
            processQueue(new Error('Token refresh failed'));
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // If refresh request fails, process queue with error
          processQueue(refreshError);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // If we're already refreshing, add this request to the queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['x-auth-token'] = token;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;